import { Box, IconButton, InputAdornment } from '@mui/material';
import { HugeiconsIcon } from '@hugeicons/react';
import { ComponentPropsWithoutRef, MouseEvent, forwardRef, useCallback, useRef } from 'react';

import { Cancel01IconStroke, Search02IconStroke } from '@/components/icons';
import { T } from '../../util/Localize';
import { TextField, TextFieldProps } from './text-field';

type HugeIcon = ComponentPropsWithoutRef<typeof HugeiconsIcon>['icon'];

/** `p: 0.25` on each side of the glyph — the button's box is `iconSize` + this. */
const CLEAR_BUTTON_PADDING_PX = 4;

export type SearchClearButtonProps = {
    /** Called when the user clicks the X. Owns resetting the search term to ''. */
    onClear: () => void;
    /** Accessible name; override when several search boxes share a screen. */
    label?: string;
    /** Match the search glyph's size so the two adornments read as a pair. */
    size?: number;
};

/**
 * The X that clears a search box. Exported on its own because a few search inputs are a raw
 * `InputBase` inside a popover (the tag / group / assignment filter dropdowns) rather than a
 * `TextField`, and so cannot use `SearchField` without being rebuilt.
 *
 * The `onMouseDown` preventDefault keeps the click from blurring the input first — otherwise a
 * dropdown that closes on blur would close before the clear ever ran.
 */
export const SearchClearButton = ({ onClear, label, size = 16 }: SearchClearButtonProps) => (
    <IconButton
        aria-label={label ?? T('Clear search')}
        size="small"
        onMouseDown={(event: MouseEvent) => event.preventDefault()}
        onClick={onClear}
        sx={{
            color: 'text.secondary',
            p: 0.25,
            /* Fixed box so the reserved slot in `SearchField` is exactly the button's size. */
            width: size + CLEAR_BUTTON_PADDING_PX,
            height: size + CLEAR_BUTTON_PADDING_PX
        }}
    >
        <HugeiconsIcon icon={Cancel01IconStroke} size={size} strokeWidth={1.5} />
    </IconButton>
);

export type SearchFieldProps = Omit<TextFieldProps, 'value'> & {
    /** Current search term. The clear button only appears while this is non-empty. */
    value: string;
    /** Reset the search term. The X calls this, then focus returns to the input. */
    onClear: () => void;
    /** Search glyph. Both `Search01IconStroke` and `Search02IconStroke` are in use. */
    searchIcon?: HugeIcon;
    /** Pixel size for both the search glyph and the clear glyph. */
    iconSize?: number;
    /** `end` puts the search glyph on the trailing edge; the X then replaces it while typing. */
    iconPosition?: 'start' | 'end';
    /** Accessible name for the X, when a bare "Clear search" is ambiguous on the page. */
    clearLabel?: string;
    /**
     * Keep the X's slot in the layout even while it is empty, so the field does not change width
     * the moment the user types. Defaults to on for anything that is not `fullWidth`, because a
     * content-sized field is exactly the one that grows — and on a right-justified box (the
     * `.searchbox` toolbars) it grows leftwards, shoving the whole toolbar sideways. A `fullWidth`
     * field is sized by its container and cannot change width, so it defaults to off and only
     * loses text room to the X once there is something to clear.
     */
    reserveClearSpace?: boolean;
};

/**
 * The house search bar: a search glyph, and — once something is typed — an X inside the field
 * that clears it. See `docs/STYLE.md` ("Search bars") for when to reach for this rather than a
 * plain `TextField`.
 *
 * Wraps the internal `TextField`, not MUI's, so `aria-label` reaches the real `<input>` (the
 * ROBO-324 class of bug) and `value` is null-normalized.
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>((props, ref) => {
    const {
        value,
        onClear,
        searchIcon = Search02IconStroke,
        iconSize = 20,
        iconPosition = 'start',
        clearLabel,
        disabled,
        slotProps,
        InputProps,
        sx,
        fullWidth,
        reserveClearSpace = !fullWidth,
        ...rest
    } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);

    const mergedRef = useCallback(
        (node: HTMLInputElement | null) => {
            inputRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
        },
        [ref]
    );

    /* Clearing from the X leaves focus in the field so the user can retype straight away. */
    const handleClear = useCallback(() => {
        onClear();
        inputRef.current?.focus();
    }, [onClear]);

    const showClear = value !== '' && value != null && !disabled;

    const searchAdornment = <HugeiconsIcon icon={searchIcon} size={iconSize} strokeWidth={1.5} />;
    const clearAdornment = (
        <SearchClearButton onClear={handleClear} label={clearLabel} size={iconSize} />
    );

    /*
     * With the glyph on the trailing edge there is only one slot, so the X takes it over while
     * there is a term to clear — no layout shift, and the field never grows a second trailing
     * control. With the glyph leading (the common case) the two sit on opposite edges.
     */
    const startAdornment =
        iconPosition === 'start' ? (
            <InputAdornment position="start">{searchAdornment}</InputAdornment>
        ) : undefined;

    const endContent =
        iconPosition === 'end' ? (
            showClear ? (
                clearAdornment
            ) : (
                searchAdornment
            )
        ) : showClear ? (
            clearAdornment
        ) : reserveClearSpace ? (
            /*
             * The empty half of the reserved slot: an inert box the exact size of the
             * button, so the field's width is the same before and after the first keystroke.
             */
            <Box
                aria-hidden
                sx={{
                    width: iconSize + CLEAR_BUTTON_PADDING_PX,
                    height: iconSize + CLEAR_BUTTON_PADDING_PX,
                    flexShrink: 0
                }}
            />
        ) : null;

    const endAdornment = endContent ? (
        <InputAdornment position="end">{endContent}</InputAdornment>
    ) : undefined;

    const inputSlotProps = {
        ...InputProps,
        ...(slotProps?.input as object | undefined),
        ...(startAdornment ? { startAdornment } : {}),
        ...(endAdornment ? { endAdornment } : {})
    };

    return (
        <TextField
            {...rest}
            value={value}
            disabled={disabled}
            fullWidth={fullWidth}
            inputRef={mergedRef}
            sx={{
                /*
                 * Call sites that set `type="search"` get a native WebKit clear button as well;
                 * hide it so there is exactly one X, ours, in every browser.
                 */
                '& input[type=search]::-webkit-search-cancel-button': { display: 'none' },
                ...sx
            }}
            slotProps={{ ...slotProps, input: inputSlotProps }}
        />
    );
});

SearchField.displayName = 'SearchField';
